'use client';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
    getHybridActivityRows,
    getHybridActivityUpdatedAt,
} from '../../../../api/hybridActivity';
import HybridActivityLog from '../HybridActivityLog';
import HybridActivityMap from '../HybridActivityMap';
import {
    selectDateExtent,
    selectPointsInDateRange,
    toDate,
} from '../HybridActivityMap/hybridActivityDates';
import {
    selectConfiguredPoints,
    toLayerConfig,
} from '../HybridActivityMap/hybridActivityLayerConfig';
import { rowsToGeoJson } from '../HybridActivityMap/hybridActivityPoints';
import { useHybridActivityDateRange } from '../HybridActivityMap/useHybridActivityDateRange';

import s from './HybridActivityTab.module.scss';

/**
 * The hybrid activity tab: the map, an editor's chart sections, and the
 * incident log.
 *
 * The point fetch and the date window live here rather than in the map because
 * the map and the log are siblings showing the same thing — the map answers
 * "where?", the log answers "what exactly happened?" — and two siblings cannot
 * share a private fetch. Layer visibility stays the map's own: which layers
 * are drawn is a map concern, and the log lists hidden layers' rows too.
 */
const HybridActivityTab = ({
    countryCode,
    hybridActivity,
    isPreview,
    htmlPath,
    children,
}) => {
    // null until the dataset answers, so "still loading" and "nothing to show"
    // stay distinguishable.
    const [rows, setRows] = useState(null);
    const [hasFailed, setHasFailed] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);

    const config = useMemo(
        () => toLayerConfig(hybridActivity),
        [hybridActivity]
    );

    useEffect(() => {
        if (!config.dataset) {
            return undefined;
        }

        let isCancelled = false;

        getHybridActivityRows(config.dataset)
            .then((fetched) => {
                if (!isCancelled) {
                    setRows(fetched);
                }
            })
            .catch((error) => {
                console.error('Error fetching hybrid activity data:', error);
                // Kept apart from the empty state: an editor has to be able to
                // tell a broken dataset from one with nothing in it.
                if (!isCancelled) {
                    setRows([]);
                    setHasFailed(true);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [config.dataset]);

    useEffect(() => {
        if (!config.dataset) {
            return undefined;
        }

        let isCancelled = false;

        getHybridActivityUpdatedAt(config.dataset)
            .then((fetched) => {
                if (!isCancelled) {
                    setUpdatedAt(fetched);
                }
            })
            .catch(() => {});

        return () => {
            isCancelled = true;
        };
    }, [config.dataset]);

    // Only rows a configured layer claims get shown, because only those have an
    // icon, a colour and a toggle in the picker.
    const { points, unclaimedValues } = useMemo(() => {
        if (rows === null) {
            return { points: null, unclaimedValues: [] };
        }

        return selectConfiguredPoints(rowsToGeoJson(rows), config);
    }, [rows, config]);

    // Spans every configured point rather than the visible ones, for the same
    // reason the map's framing does: the control a reader is holding should not
    // resize under them when they switch a layer off.
    const dateExtent = useMemo(
        () => selectDateExtent(points, config),
        [points, config]
    );

    const lastUpdated =
        toDate(String(updatedAt ?? '').slice(0, 10)) ?? dateExtent?.max;

    const { range, appliedRange, selectRange } =
        useHybridActivityDateRange(dateExtent);

    // The one filtered set both views read: what the map plots — before layer
    // visibility, which is its own to apply — and what the log lists.
    const pointsInWindow = useMemo(
        () => selectPointsInDateRange(points, config, appliedRange),
        [points, config, appliedRange]
    );

    return (
        <>
            {/* Full tab height, and shrink-proof: the chart sections and the
                log below overflow into the section's own scroll rather than
                squeezing the map. */}
            <div className={s.MapArea}>
                <HybridActivityMap
                    countryCode={countryCode}
                    isPreview={isPreview}
                    htmlPath={htmlPath}
                    config={config}
                    points={points}
                    pointsInWindow={pointsInWindow}
                    unclaimedValues={unclaimedValues}
                    hasFailed={hasFailed}
                    dateExtent={dateExtent}
                    lastUpdated={lastUpdated}
                    range={range}
                    appliedRange={appliedRange}
                    onSelectRange={selectRange}
                />
            </div>

            {children}

            <HybridActivityLog
                points={points}
                pointsInWindow={pointsInWindow}
                config={config}
                dateExtent={dateExtent}
            />
        </>
    );
};

HybridActivityTab.propTypes = {
    countryCode: PropTypes.string,
    // Authored once on the parent Watchtower page, so every country tab under
    // it draws the same layers from the same dataset.
    hybridActivity: PropTypes.shape({
        dataset: PropTypes.string,
        layerColumn: PropTypes.string,
        layers: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                matchValue: PropTypes.string,
                icon: PropTypes.string,
                colour: PropTypes.string,
                isDated: PropTypes.bool,
            })
        ),
    }),
    isPreview: PropTypes.bool,
    htmlPath: PropTypes.string,
    // The editor-composed chart sections, slotted between the map and the log.
    children: PropTypes.node,
};

export default HybridActivityTab;
